import React, { useCallback, useMemo } from 'react';
import { Command } from 'cmdk';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { releaseService, projectService, sessionService } from '@/app/providers/services';
import { getReleaseUiName, useLoadScopeByRelease } from '@/features/release';
import { setScope, useScope } from '@/redux/scope';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { CommandItem } from '../components/CommandItem';
import { CommandsSkeleton } from '../components/CommandsSkeleton';
import {
  useDebouncedSearch,
  usePluginNavigation,
  useRegisterCommands,
  useRegisterPage,
} from '../hooks';
import type { Command as CommandType } from '../types';
import styles from '../CommandPalette.module.scss';
import { cn, isGuid } from '@/lib/utils';

// Users sometimes paste ids copied with a leading "#" (e.g. from a UI badge).
// Strip it so the value can be validated as a plain GUID before it's ever
// used to build a request path — an unstripped "#" is parsed as a URL
// fragment and silently truncates the request to the collection endpoint.
const normalizeId = (value: string) => value.trim().replace(/^#/, '');

const PLUGIN_ID = 'find-by-id-plugin';
const RELEASE_BY_ID_PAGE = 'find-release-by-id';
const PROJECT_BY_ID_PAGE = 'find-project-by-id';
const SESSION_BY_ID_PAGE = 'find-session-by-id';
const RELEASE_BY_ID_PLACEHOLDER = 'Paste a release ID...';
const PROJECT_BY_ID_PLACEHOLDER = 'Paste a project ID...';
const SESSION_BY_ID_PLACEHOLDER = 'Paste a session ID...';

// ---------------------------------------------------------------------------
// FindReleaseByIdPage — paste a release ID to load full scope
// (workspace + project + release)
// ---------------------------------------------------------------------------

const FindReleaseByIdPage: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const loadScopeByRelease = useLoadScopeByRelease();
  const rawSearch = useDebouncedSearch(300);
  const releaseId = normalizeId(rawSearch);

  const { data: release, isLoading, isError } = releaseService.useGetOne(releaseId, {
    params: { graph: false },
    enabled: isGuid(releaseId),
    retry: false,
  });

  const handleSelect = useCallback(async () => {
    if (!release) return;
    await loadScopeByRelease(release.id);
    dispatch(push('/build'));
    close();
  }, [release, loadScopeByRelease, dispatch, close]);

  const command = useMemo((): CommandType | null => {
    if (!release) return null;
    const name = getReleaseUiName(release);
    const releaseNum = release.releaseNo != null ? `R${release.releaseNo}` : undefined;
    const parts = [release.workspace_name, release.model_name, releaseNum].filter(Boolean);
    return {
      id: `find-release-by-id-${release.id}`,
      label: name,
      description: parts.length > 0 ? parts.join(' · ') : undefined,
      category: 'Releases',
      keywords: [release.id, String(release.releaseNo ?? '')],
      primary: {
        id: `find-release-by-id-${release.id}-load`,
        label: 'Load Release',
        action: handleSelect,
      },
    };
  }, [release, handleSelect]);

  if (!releaseId) {
    return (
      <Command.List className={cn(styles.commandList, 'scrollable')}>
        <Command.Empty>Paste a release ID into the search box above.</Command.Empty>
      </Command.List>
    );
  }

  if (isLoading) {
    return <CommandsSkeleton count={1} />;
  }

  if (isError || !command) {
    return (
      <Command.List className={cn(styles.commandList, 'scrollable')}>
        <Command.Empty>No release found with that ID.</Command.Empty>
      </Command.List>
    );
  }

  return (
    <Command.List className={cn(styles.commandList, 'scrollable')}>
      <CommandItem command={command} onSelect={handleSelect} />
    </Command.List>
  );
};

// ---------------------------------------------------------------------------
// FindProjectByIdPage — paste a project ID to load workspace + project scope.
// ---------------------------------------------------------------------------

const FindProjectByIdPage: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const scope = useScope();
  const rawSearch = useDebouncedSearch(300);
  const projectId = normalizeId(rawSearch);

  const { data: project, isLoading, isError } = projectService.useGetOne(projectId, {
    enabled: isGuid(projectId),
    retry: false,
  });

  const handleSelect = useCallback(() => {
    if (!project) return;
    dispatch(setScope({
      ...scope,
      workspace: project.workspace,
      project: project.id,
      release: null,
    }));
    dispatch(push('/build'));
    close();
  }, [project, scope, dispatch, close]);

  const command = useMemo((): CommandType | null => {
    if (!project) return null;
    return {
      id: `find-project-by-id-${project.id}`,
      label: project.name ?? project.id,
      description: project.workspace_name,
      category: 'Projects',
      keywords: [project.id, project.name].filter(Boolean) as string[],
      primary: {
        id: `find-project-by-id-${project.id}-load`,
        label: 'Open Project',
        action: handleSelect,
      },
    };
  }, [project, handleSelect]);

  if (!projectId) {
    return (
      <Command.List className={cn(styles.commandList, 'scrollable')}>
        <Command.Empty>Paste a project ID into the search box above.</Command.Empty>
      </Command.List>
    );
  }

  if (isLoading) {
    return <CommandsSkeleton count={1} />;
  }

  if (isError || !command) {
    return (
      <Command.List className={cn(styles.commandList, 'scrollable')}>
        <Command.Empty>No project found with that ID.</Command.Empty>
      </Command.List>
    );
  }

  return (
    <Command.List className={cn(styles.commandList, 'scrollable')}>
      <CommandItem command={command} onSelect={handleSelect} />
    </Command.List>
  );
};

// ---------------------------------------------------------------------------
// FindSessionByIdPage — paste a session ID to open it in the monitor modal.
// Navigates to /monitor/overview/:sessionId which auto-opens the SessionModal.
// Does not change workspace/project/release scope.
// ---------------------------------------------------------------------------

const FindSessionByIdPage: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const loadScopeByRelease = useLoadScopeByRelease();
  const rawSearch = useDebouncedSearch(300);
  const sessionId = normalizeId(rawSearch);

  const { data: session, isLoading, isError } = sessionService.useGetOne(sessionId, {
    params: { graph: false },
    enabled: isGuid(sessionId),
    retry: false,
  });

  const releaseId = session?.release as string | undefined;

  const { data: release } = releaseService.useGetOne(releaseId ?? '', {
    params: { graph: false },
    enabled: !!releaseId,
    retry: false,
  });

  const handleSelect = useCallback(async () => {
    if (!session) return;
    if (releaseId) await loadScopeByRelease(releaseId);
    dispatch(push(`/monitor/overview/${session.id}`));
    close();
  }, [session, releaseId, loadScopeByRelease, dispatch, close]);

  const command = useMemo((): CommandType | null => {
    if (!session) return null;

    const status = session.status as string | undefined;
    const lastModified = session.lastModified as string | undefined;
    const lastUpdated = lastModified ? new Date(lastModified).toLocaleString() : undefined;
    const labelParts = [status, lastUpdated ? `last updated ${lastUpdated}` : undefined].filter(Boolean);

    const releaseNum = release?.releaseNo != null ? `R${release.releaseNo}` : undefined;
    const descParts = [release?.workspace_name, release?.model_name, releaseNum].filter(Boolean);

    return {
      id: `find-session-by-id-${session.id}`,
      label: labelParts.length > 0 ? labelParts.join(', ') : session.id,
      description: descParts.length > 0 ? descParts.join(' > ') : undefined,
      category: 'Sessions',
      keywords: [session.id],
      primary: {
        id: `find-session-by-id-${session.id}-open`,
        label: 'Open Session',
        action: handleSelect,
      },
    };
  }, [session, release, handleSelect]);

  if (!sessionId) {
    return (
      <Command.List className={cn(styles.commandList, 'scrollable')}>
        <Command.Empty>Paste a session ID into the search box above.</Command.Empty>
      </Command.List>
    );
  }

  if (isLoading) {
    return <CommandsSkeleton count={1} />;
  }

  if (isError || !command) {
    return (
      <Command.List className={cn(styles.commandList, 'scrollable')}>
        <Command.Empty>No session found with that ID.</Command.Empty>
      </Command.List>
    );
  }

  return (
    <Command.List className={cn(styles.commandList, 'scrollable')}>
      <CommandItem command={command} onSelect={handleSelect} />
    </Command.List>
  );
};

// ---------------------------------------------------------------------------
// FindByIdPlugin — registers global commands and pages for find-by-id flows
// ---------------------------------------------------------------------------

/**
 * FindByIdPlugin - Look up a release or project by its exact ID.
 *
 * Find Release by ID: paste a release ID to set the full scope
 * (workspace, project, release) and navigate to the build view.
 *
 * Find Project by ID: paste a project ID to set the workspace and project
 * in scope and navigate to the build view. The workspace name is shown
 * in the result description.
 */
export const FindByIdPlugin: React.FC = () => {
  const { navigateTo } = usePluginNavigation(PLUGIN_ID);

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'find-release-by-id',
      label: 'Find Release by ID',
      description: 'Paste a release ID to load it directly',
      category: 'Navigation',
      keywords: ['release', 'id', 'find', 'load', 'lookup'],
      primary: {
        id: 'find-release-by-id-action',
        label: 'Find Release',
        action: () => navigateTo(RELEASE_BY_ID_PAGE, RELEASE_BY_ID_PLACEHOLDER),
      },
    },
    {
      id: 'find-project-by-id',
      label: 'Find Project by ID',
      description: 'Paste a project ID to load it directly',
      category: 'Navigation',
      keywords: ['project', 'id', 'find', 'load', 'lookup'],
      primary: {
        id: 'find-project-by-id-action',
        label: 'Find Project',
        action: () => navigateTo(PROJECT_BY_ID_PAGE, PROJECT_BY_ID_PLACEHOLDER),
      },
    },
    {
      id: 'find-session-by-id',
      label: 'Find Session by ID',
      description: 'Paste a session ID to open it in the monitor',
      category: 'Navigation',
      keywords: ['session', 'id', 'find', 'open', 'lookup', 'monitor'],
      primary: {
        id: 'find-session-by-id-action',
        label: 'Find Session',
        action: () => navigateTo(SESSION_BY_ID_PAGE, SESSION_BY_ID_PLACEHOLDER),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: RELEASE_BY_ID_PAGE, component: FindReleaseByIdPage });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: PROJECT_BY_ID_PAGE, component: FindProjectByIdPage });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: SESSION_BY_ID_PAGE, component: FindSessionByIdPage });

  return null;
};
